import { useEffect, useMemo, useState } from "react";
import {
  Plus, Edit, Trash2, Tag, Copy, Check, Loader2, Search, Power, PowerOff, X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { toast } from "sonner";

const blank = {
  code: "",
  percent: 10,
  active: true,
  description: "",
  max_uses: "",
};

export default function AdminDiscounts() {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/discounts");
      setCodes(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Konnte Codes nicht laden");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!search.trim()) return codes;
    const q = search.toLowerCase();
    return codes.filter(
      (c) => c.code.toLowerCase().includes(q) || (c.description || "").toLowerCase().includes(q)
    );
  }, [codes, search]);

  const startCreate = () => {
    setEditing(null);
    setForm(blank);
    setOpen(true);
  };

  const startEdit = (c) => {
    setEditing(c);
    setForm({
      code: c.code,
      percent: c.percent,
      active: !!c.active,
      description: c.description || "",
      max_uses: c.max_uses == null ? "" : String(c.max_uses),
    });
    setOpen(true);
  };

  const submit = async () => {
    const code = form.code.trim().toUpperCase();
    const pct = Number(form.percent);
    if (!code) return toast.error("Code darf nicht leer sein");
    if (!(pct >= 1 && pct <= 100)) return toast.error("Prozent muss zwischen 1 und 100 liegen");
    const payload = {
      code,
      percent: pct,
      active: !!form.active,
      description: (form.description || "").trim(),
      max_uses: form.max_uses === "" ? null : Math.max(1, parseInt(form.max_uses, 10) || 0),
    };
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/admin/discounts/${editing.id}`, payload);
        toast.success(`Code "${code}" aktualisiert`);
      } else {
        await api.post("/admin/discounts", payload);
        toast.success(`Code "${code}" angelegt`);
      }
      setOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Code "${c.code}" wirklich löschen?`)) return;
    try {
      await api.delete(`/admin/discounts/${c.id}`);
      toast.success(`Code "${c.code}" gelöscht`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Löschen fehlgeschlagen");
    }
  };

  const toggleActive = async (c) => {
    try {
      await api.patch(`/admin/discounts/${c.id}`, {
        code: c.code,
        percent: c.percent,
        active: !c.active,
        description: c.description || "",
        max_uses: c.max_uses,
      });
      toast.success(c.active ? `"${c.code}" deaktiviert` : `"${c.code}" aktiviert`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Status-Wechsel fehlgeschlagen");
    }
  };

  const copyCode = (c) => {
    navigator.clipboard.writeText(c.code).then(() => {
      setCopiedId(c.id);
      setTimeout(() => setCopiedId(null), 1500);
    });
  };

  return (
    <div data-testid="admin-discounts-page" className="text-white">
      {/* HEADER */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#FF2D40] font-bold">Rabatte</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-1">Rabatt-Codes</h1>
          <p className="text-sm text-zinc-500 mt-1.5">
            Lege Codes an, die Kunden im Checkout eingeben — z. B. <span className="text-zinc-300 font-mono">SOMMER10</span> für 10% Rabatt.
          </p>
        </div>
        <Button onClick={startCreate} className="bg-[#DC1928] hover:bg-[#B01829] rounded-full h-11 px-5" data-testid="discount-create-btn">
          <Plus className="w-4 h-4 mr-1.5" /> Neuer Code
        </Button>
      </div>

      {/* SEARCH */}
      <div className="relative mb-5 max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Suche nach Code oder Beschreibung…"
          className="pl-9 bg-[#141417] border-white/10 text-white placeholder:text-zinc-600 focus:border-[#DC1928] focus:ring-0 h-10"
          data-testid="discount-search"
        />
      </div>

      {/* LIST */}
      {loading ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-32 bg-[#141417] border border-white/5 rounded-2xl animate-pulse"></div>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="p-10 rounded-3xl border border-dashed border-white/10 bg-[#0F0F11] text-center" data-testid="discount-empty">
          <Tag className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
          <div className="font-display text-lg font-semibold text-white">Noch keine Codes</div>
          <p className="text-sm text-zinc-500 mt-1 max-w-md mx-auto">
            Lege deinen ersten Rabatt-Code an. Beispiel: <span className="font-mono text-zinc-300">SOMMER10</span> = 10% Rabatt.
          </p>
          <Button onClick={startCreate} className="mt-5 bg-[#DC1928] hover:bg-[#B01829] rounded-full h-10 px-5" data-testid="discount-empty-create">
            <Plus className="w-4 h-4 mr-1.5" /> Ersten Code anlegen
          </Button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="discount-grid">
          {filtered.map((c) => (
            <article
              key={c.id}
              className={`relative rounded-3xl border p-5 flex flex-col gap-3 transition-colors ${
                c.active
                  ? "border-white/8 bg-gradient-to-br from-[#15151A] via-[#101013] to-[#0A0A0B]"
                  : "border-white/5 bg-[#0F0F11] opacity-70"
              }`}
              data-testid={`discount-card-${c.code}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-mono text-2xl font-bold tracking-wider text-white truncate">{c.code}</div>
                  {c.description && <div className="text-xs text-zinc-500 mt-0.5 line-clamp-2">{c.description}</div>}
                </div>
                <div className="px-3 py-1 rounded-full bg-[#DC1928]/12 border border-[#DC1928]/30 text-[#FF4F5E] font-display font-bold text-sm shrink-0 numeric">
                  -{c.percent}%
                </div>
              </div>

              <div className="flex items-center gap-4 text-[11px] text-zinc-500 mt-1">
                <span className="inline-flex items-center gap-1">
                  <span className={`w-1.5 h-1.5 rounded-full ${c.active ? "bg-emerald-400" : "bg-zinc-600"}`}></span>
                  {c.active ? "Aktiv" : "Deaktiviert"}
                </span>
                <span>
                  {c.usage_count}× verwendet
                  {c.max_uses ? ` / ${c.max_uses} max.` : ""}
                </span>
              </div>

              <div className="flex items-center gap-1.5 pt-2 mt-auto border-t border-white/5">
                <button
                  type="button"
                  onClick={() => copyCode(c)}
                  className="flex-1 h-9 inline-flex items-center justify-center gap-1.5 text-xs text-zinc-400 hover:text-white bg-white/5 hover:bg-white/10 rounded-full border border-white/8 transition-colors"
                  data-testid={`discount-copy-${c.code}`}
                >
                  {copiedId === c.id ? <><Check className="w-3.5 h-3.5 text-emerald-400" /> Kopiert</> : <><Copy className="w-3.5 h-3.5" /> Code</>}
                </button>
                <button
                  type="button"
                  onClick={() => toggleActive(c)}
                  className={`w-9 h-9 inline-flex items-center justify-center rounded-full border transition-colors ${
                    c.active
                      ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/20"
                      : "bg-zinc-500/10 border-zinc-500/30 text-zinc-400 hover:bg-zinc-500/20"
                  }`}
                  title={c.active ? "Deaktivieren" : "Aktivieren"}
                  data-testid={`discount-toggle-${c.code}`}
                >
                  {c.active ? <Power className="w-4 h-4" /> : <PowerOff className="w-4 h-4" />}
                </button>
                <button
                  type="button"
                  onClick={() => startEdit(c)}
                  className="w-9 h-9 inline-flex items-center justify-center rounded-full bg-white/5 hover:bg-white/10 border border-white/8 text-zinc-300"
                  title="Bearbeiten"
                  data-testid={`discount-edit-${c.code}`}
                >
                  <Edit className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => remove(c)}
                  className="w-9 h-9 inline-flex items-center justify-center rounded-full bg-red-500/10 hover:bg-red-500/20 border border-red-500/25 text-red-300"
                  title="Löschen"
                  data-testid={`discount-delete-${c.code}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </article>
          ))}
        </div>
      )}

      {/* DIALOG */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#141417] border-white/10 text-white sm:max-w-md" data-testid="discount-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              {editing ? "Code bearbeiten" : "Neuer Rabatt-Code"}
            </DialogTitle>
            <DialogDescription className="text-zinc-500 text-sm">
              {editing ? `Anpassen von "${editing.code}".` : "Lege einen Code an, den Kunden im Checkout eingeben können."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 font-semibold">Code</Label>
              <Input
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                placeholder="SOMMER10"
                className="mt-1.5 bg-[#0A0A0B] border-white/10 text-white font-mono uppercase tracking-wider focus:border-[#DC1928] focus:ring-0 h-11"
                data-testid="discount-form-code"
                maxLength={32}
              />
              <p className="text-[10px] text-zinc-600 mt-1">Wird automatisch in Großbuchstaben gespeichert.</p>
            </div>

            <div>
              <Label className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 font-semibold">Rabatt (%)</Label>
              <div className="mt-1.5 relative">
                <Input
                  type="number"
                  min={1}
                  max={100}
                  value={form.percent}
                  onChange={(e) => setForm({ ...form, percent: e.target.value })}
                  className="bg-[#0A0A0B] border-white/10 text-white focus:border-[#DC1928] focus:ring-0 h-11 pr-10 numeric"
                  data-testid="discount-form-percent"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 font-display font-semibold">%</span>
              </div>
              <p className="text-[10px] text-zinc-600 mt-1">Zwischen 1 und 100. Wird auf den Bestellpreis angewendet.</p>
            </div>

            <div>
              <Label className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 font-semibold">Max. Einlösungen (optional)</Label>
              <Input
                type="number"
                min={1}
                value={form.max_uses}
                onChange={(e) => setForm({ ...form, max_uses: e.target.value })}
                placeholder="Leer = unbegrenzt"
                className="mt-1.5 bg-[#0A0A0B] border-white/10 text-white focus:border-[#DC1928] focus:ring-0 h-11 numeric"
                data-testid="discount-form-max-uses"
              />
            </div>

            <div>
              <Label className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 font-semibold">Beschreibung (intern)</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="z. B. Black Friday Aktion 2026"
                rows={2}
                className="mt-1.5 bg-[#0A0A0B] border-white/10 text-white placeholder:text-zinc-600 focus:border-[#DC1928] focus:ring-0 resize-none"
                data-testid="discount-form-description"
              />
            </div>

            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={!!form.active}
                onChange={(e) => setForm({ ...form, active: e.target.checked })}
                className="accent-[#DC1928] w-4 h-4"
                data-testid="discount-form-active"
              />
              <span className="text-sm text-zinc-300">Sofort aktivieren</span>
            </label>
          </div>

          <DialogFooter className="mt-5">
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              className="bg-transparent border-white/15 text-zinc-300 hover:bg-white/5 hover:text-white rounded-full h-11 px-5"
              data-testid="discount-form-cancel"
            >
              <X className="w-4 h-4 mr-1" /> Abbrechen
            </Button>
            <Button
              onClick={submit}
              disabled={saving}
              className="bg-[#DC1928] hover:bg-[#B01829] text-white rounded-full h-11 px-5 disabled:opacity-50"
              data-testid="discount-form-submit"
            >
              {saving ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Speichere…</> : (editing ? "Speichern" : "Code anlegen")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
